import axios from "axios";
import camelKeys from "camelcase-keys";
import { getBaseHeaders, getHeadersWithContentType } from "../infrastructure/http/apiHeaderUtility";
import { Country } from "src/components/Context/CountryContext";
import {
  GetProductResponse,
  GetPromotionsResponse,
  GetPurchaseOrdersResponse,
  GetInventoryTransactionsResponse,
} from "src/models/product";
import { GetAppDataResponse, GetListsResponse, GetProductListProductResponse } from "src/models/appData";
import { PaginatedSearchResults, SearchResponse, SearchResult, SearchResults } from "src/models/search";
import { MPPricingHistoryResponse } from "src/models/product/MPPricingHistoryResponse";
import { JBPricingHistoryResponse } from "src/models/product/JBPricingHistoryResponse";
import { CreateMessageModel } from "src/validationSchemas";
import { CreateExceptionModel } from "src/validationSchemas/createExceptionSchema";
import { ProductHistoryResponse } from "../models/product/ProductHistoryResponse";
import { ProductGroupResponse } from "src/models/product/ProductGroupResponse";
import { ProductCategoryResponse } from "src/models/product/ProductCategoryResponse";
import { PhysicalUserStoreLocationResponse } from "src/models/appData/PhysicalUserStoreLocationResponse";
import { WasPriceOverrideResponse } from "src/models/product/WasPriceOverrideResponse";
import { GetLocationsResponse } from "src/models/locations/GetLocationsResponse";
import { StaffExceptionModel } from "src/models/staffPriceExceptions/StaffExceptionModel";

const getSearchAsync = async (
  query: string,
  jbPage: number,
  mpPage: number,
  pageSize: number,
  country: Country
): Promise<SearchResponse> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_APIM_URL}/Search?q=${query}&jbPage=${jbPage}&mpPage=${mpPage}&pageSize=${pageSize}`,
        {
          headers,
        }
      )
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getLocations = async (country: Country): Promise<GetLocationsResponse> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Locations`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getSearchMessagingProductsAsync = async (
  query: string,
  page: number,
  pageSize: number,
  country: Country
): Promise<SearchResults> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Search/Messaging?q=${query}&page=${page}&pageSize=${pageSize}`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getSearchMessagingProductsBySkusAsync = async (
  skus: string[],
  country: Country,
  page: number,
  pageSize: number
): Promise<PaginatedSearchResults> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Search/Messaging/BySkus`, JSON.stringify({ skus, page, pageSize }), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getProductAsync = async (sku: string, country: Country, locationId?: number): Promise<GetProductResponse> => {
  const headers = await getHeadersWithContentType(country);
  var locationString = locationId ? `&locationId=${locationId}` : "";
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/product?sku=${sku}${locationString}`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getMPPricingHistoryAsync = async (sku: string, country: Country): Promise<MPPricingHistoryResponse> => {
  const headers = await getHeadersWithContentType(country);

  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/PriceHistory/MP/${sku}`, { headers })
      .then((response) => {
        resolve(camelKeys(response.data));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getJBPricingHistoryAsync = async (sku: string, country: Country): Promise<JBPricingHistoryResponse[]> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/PriceHistory/JB/${sku}`, { headers })
      .then((response) => {
        resolve(camelKeys(response.data));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getProductHistoryAsync = async (sku: string, country: Country): Promise<ProductHistoryResponse> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/ProductHistory/${sku}`, { headers })
      .then((response) => {
        resolve(camelKeys(response.data));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getBrandImageAsync = async (brandName: string, country: Country): Promise<string> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/BrandImage`, {
        params: { brandName },
        headers,
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postBrandImageAsync = async (brandName: string, image: any, country: Country) => {
  const formData = new FormData();
  formData.append("brandName", brandName);

  if (image) {
    formData.append("image", image);
  }

  var headers = await getBaseHeaders(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/BrandImage`, formData, {
        headers,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postWasPricesAsync = async (wasPrices, country): Promise<WasPriceOverrideResponse | null> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/WasPriceOverride`, JSON.stringify(wasPrices), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const patchProductAsync = async (patch, sku, country) => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .patch(`${process.env.REACT_APP_APIM_URL}/Product/${sku}`, patch, {
        headers,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getInventoryTransactionsAsync = async (
  sku: string,
  locationId: number,
  country: Country,
  includeActive: boolean
): Promise<GetInventoryTransactionsResponse | null> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_APIM_URL}/Inventory/Transactions?sku=${sku}&locationId=${locationId}&includeActive=${includeActive}`,
        {
          headers,
        }
      )
      .then((response) => {
        resolve(camelKeys(response.data || null, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getPurchaseOrdersAsync = async (sku: string, country: Country): Promise<GetPurchaseOrdersResponse[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/PurchaseOrders/Sku/${sku}?status=Released`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getPromosAsync = async (sku: string, locationId: number, country: Country): Promise<GetPromotionsResponse[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Product/${sku}/Promotions?LocationId=${locationId}`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys<GetPromotionsResponse[]>(response.data || [], { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getPinnedProducts = async (country: Country): Promise<SearchResult[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Users/Products/Pinned/Details`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postPinnedProducts = async (pinnedProducts: string[], country: Country) => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Users/Products/Pinned`, JSON.stringify(pinnedProducts), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getMyLists = async (country: Country): Promise<GetListsResponse[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Users/Products/Lists`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getMyListDetails = async (name: string, country: Country): Promise<GetProductListProductResponse[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Users/Products/Lists/Details`, {
        headers,
        params: { name },
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postMyListItem = async (requestObject: any, country: Country) => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Users/Products/Lists/Items`, JSON.stringify(requestObject), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const deleteMyList = async (requestObject: any, country: Country): Promise<void> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise<void>((resolve, reject) => {
    axios
      .delete(`${process.env.REACT_APP_APIM_URL}/Users/Products/Lists`, {
        headers,
        data: JSON.stringify(requestObject),
      })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const deleteMyListItem = async (requestObject: any, country: Country) => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .delete(`${process.env.REACT_APP_APIM_URL}/Users/Products/Lists/Items`, {
        headers,
        data: JSON.stringify(requestObject),
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getInitialAppData = async (country: Country): Promise<GetAppDataResponse> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/AppData`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getReleaseNotes = async (country: Country): Promise<any[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/ReleaseNotes`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getLatestMessagesAsync = async (country: Country): Promise<CreateMessageModel[]> => {
  const headers = await getHeadersWithContentType(country);
  const url = `${process.env.REACT_APP_APIM_URL}/Messages/Recent`;
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getMessageByIdAsync = async (id: string, country: Country): Promise<CreateMessageModel> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/Messages/${id}`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getMessagesBySearchTextAsync = async (searchtext: string, country: Country): Promise<CreateMessageModel[]> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_APIM_URL}/Messages/FindMessagesBySkuAndName?searchtext=${encodeURIComponent(
          searchtext
        )}`,
        {
          headers,
        }
      )
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postMessageTemplateCreateAsync = async (requestObject: any, country: Country): Promise<any> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Messages`, JSON.stringify(requestObject), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const putMessageTemplateUpdateAsync = async (id: string, requestObject: any, country: Country): Promise<any> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .put(`${process.env.REACT_APP_APIM_URL}/Messages/${id}`, JSON.stringify(requestObject), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postMessageImageAsync = async (image: File, country: Country): Promise<any> => {
  const formData = new FormData();
  formData.append("image", image);

  var headers = await getBaseHeaders(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Messages/Image`, formData, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postMessageImageCopyAsync = async (image: any, country: Country): Promise<any> => {
  var headers = await getBaseHeaders(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/Messages/Image/Copy`, JSON.stringify(image), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getLatestExceptionsAsync = async (country: Country): Promise<StaffExceptionModel[]> => {
  const headers = await getHeadersWithContentType(country);
  const url = `${process.env.REACT_APP_APIM_URL}/StaffPriceExceptions`;
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postStaffPriceExceptionsAsync = async (requestObject: any, country: Country): Promise<any> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/StaffPriceExceptions`, JSON.stringify(requestObject), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postStaffPriceExceptionsBatchAsync = async (requestObject: any, country: Country): Promise<any> => {
  var headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/StaffPriceExceptions/Batch`, JSON.stringify(requestObject), {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getProductGroupAsync = async (id: string, name: string, country: Country): Promise<ProductGroupResponse[]> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/ProductGroups`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getProductCategoryAsync = async (
  id: string,
  name: string,
  country: Country
): Promise<ProductCategoryResponse[]> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_APIM_URL}/ProductCategories`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postProductSyncMappingsAsync = async (
  mappings: { auSku: string; nzSku: string }[],
  userEmail: string,
  country: Country
) => {
  const headers = await getHeadersWithContentType(country);
  const payload = {
    mappings,
    userEmail,
  };

  return axios.post(`${process.env.REACT_APP_APIM_URL}/product/au-to-nz-mapping`, JSON.stringify(payload), { headers });
};

const postProductSyncSameLocationAsync = async (
  mappings: { sourceSku: string; targetSku: string }[],
  userEmail: string,
  syncOptions: string[],
  syncType: string,
  country: Country
) => {
  const headers = await getHeadersWithContentType(country);
  const payload = {
    mappings,
    userEmail,
    syncOptions,
    syncType,
  };

  return axios.post(`${process.env.REACT_APP_APIM_URL}/product/same-location-mapping`, JSON.stringify(payload), {
    headers,
  });
};

const getPhysicalUserStoreLocationAsync = async (country: Country): Promise<PhysicalUserStoreLocationResponse> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_LOCATION_API_URL}/servers/here`, {
        headers,
      })
      .then((response) => {
        resolve(camelKeys(response.data, { deep: true }));
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const postProductSyncFromProdAsync = async (sku: string, country: Country): Promise<{ message: string }> => {
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_APIM_URL}/product/sync-from-prod-sku`, JSON.stringify({ Sku: sku }), {
        headers,
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getSearchProductionSkuAsync = async (
  query: string,
  jbPage: number,
  pageSize: number,
  country: Country
): Promise<SearchResults> => {
  // This function explicitly searches production Algolia index
  // For UAT environment, we need to query production data
  const headers = await getHeadersWithContentType(country);
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_APIM_URL}/ReplicatorSearch?q=${encodeURIComponent(
          query
        )}&jbPage=${jbPage}&pageSize=${pageSize}`,
        {
          headers,
        }
      )
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export {
  getSearchAsync,
  getSearchMessagingProductsAsync,
  getSearchMessagingProductsBySkusAsync,
  getProductAsync,
  getBrandImageAsync,
  postBrandImageAsync,
  postWasPricesAsync,
  getPurchaseOrdersAsync,
  getPromosAsync,
  getInventoryTransactionsAsync,
  patchProductAsync,
  getPinnedProducts,
  postPinnedProducts,
  getMyLists,
  getMyListDetails,
  postMyListItem,
  deleteMyList,
  deleteMyListItem,
  getInitialAppData,
  getReleaseNotes,
  getMPPricingHistoryAsync,
  getJBPricingHistoryAsync,
  getLatestMessagesAsync,
  getMessageByIdAsync,
  postMessageTemplateCreateAsync,
  putMessageTemplateUpdateAsync,
  postMessageImageAsync,
  postMessageImageCopyAsync,
  getMessagesBySearchTextAsync,
  getProductHistoryAsync,
  getLatestExceptionsAsync,
  postStaffPriceExceptionsAsync,
  postStaffPriceExceptionsBatchAsync,
  getProductGroupAsync,
  getProductCategoryAsync,
  postProductSyncMappingsAsync,
  postProductSyncSameLocationAsync,
  getPhysicalUserStoreLocationAsync,
  postProductSyncFromProdAsync,
  getSearchProductionSkuAsync,
  getLocations,
};
